import { useNamesMap } from '@/context/names/names-context';
import { useReferenceMap } from '@/context/references/references-context';
import { expandTableGrid } from '@/lib/expand-table-grid';
import { resolveRefTokens } from '@/lib/resolve-ref-tokens';
import { sanitizeBlockHtml } from '@/lib/sanitize';
import { HtmlContent } from '@/components/atoms/html-content/html-content';
import type { DocumentEntity } from '@/context/store/slices/documentDataSlice';
import {
  buildColgroupStyles,
  getColumnAlign,
} from '@worldathletics/utilities';

import './document-view-table.scss';

type Props = { node: DocumentEntity & { type: 'table' } };

function DocumentViewTable({ node }: Props) {
  const names = useNamesMap();
  const refMap = useReferenceMap();
  const computedName = names.get(node.id) ?? '';
  const totalColumns = node.columns?.length ?? undefined;
  const grid = expandTableGrid(node.rows, totalColumns);
  const colCount = grid[0]?.length ?? 0;
  const colgroupStyles = buildColgroupStyles(node.columns, colCount);
  const headerRowCount = node.headerRowCount ?? 0;
  const rowHeaderCol = node.rowHeaderCol ?? false;

  // Preserve every persisted body row — including intentionally empty ones
  // the editor authored (e.g. a 3×3 grid with only the first row filled).
  // Filtering empty rows here made preview diverge from the stored grid.
  const bodyRows = grid
    .slice(headerRowCount)
    .map((row, rOffset) => ({ row, r: headerRowCount + rOffset }));

  function cellHtml(text: string): string {
    // The source block id for a table cell reference is the TABLE block
    // itself — Reference.sourceId FKs the table, not individual cells (see
    // use-extract-ref-tokens.ts's table branch).
    return sanitizeBlockHtml(resolveRefTokens(text, refMap, node.id));
  }

  function cellStyle(colIndex: number): { textAlign?: 'left' | 'center' | 'right' } {
    const align = getColumnAlign(node.columns, colIndex);
    return align !== undefined ? { textAlign: align } : {};
  }

  return (
    <div id={node.id}>
      <table className="doc-view-table" data-type="card">
        <caption>
          {`Table ${computedName}${node.caption ? ` — ${node.caption}` : ''}`}
        </caption>
        {colCount > 0 && (
          <colgroup>
            {colgroupStyles.map((col, i) => (
              <col
                key={i}
                style={{
                  ...(col.width !== undefined ? { width: col.width } : {}),
                }}
              />
            ))}
          </colgroup>
        )}
        {headerRowCount > 0 && (
          <thead>
            {Array.from({ length: headerRowCount }).map((_, r) => (
              <tr key={r}>
                {grid[r]!
                  .filter(gc => gc.isAnchor)
                  .map(gc => (
                    <HtmlContent
                      key={`${String(gc.r)}-${String(gc.c)}`}
                      as="th"
                      rowSpan={gc.cell.rowSpan ?? 1}
                      colSpan={gc.cell.colSpan ?? 1}
                      scope="col"
                      style={cellStyle(gc.c)}
                      html={cellHtml(gc.cell.text)}
                    />
                  ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody>
          {bodyRows.map(({ row, r }) => (
            <tr key={r}>
              {row
                .filter(gc => gc.isAnchor)
                .map(gc => {
                  const isRowHeader = rowHeaderCol && gc.c === 0;
                  return (
                    <HtmlContent
                      key={`${String(gc.r)}-${String(gc.c)}`}
                      as={isRowHeader ? 'th' : 'td'}
                      rowSpan={gc.cell.rowSpan ?? 1}
                      colSpan={gc.cell.colSpan ?? 1}
                      scope={isRowHeader ? 'row' : undefined}
                      style={cellStyle(gc.c)}
                      html={cellHtml(gc.cell.text)}
                    />
                  );
                })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export { DocumentViewTable };
